import { BaseDropdownOption } from "@/components/dropdowns/BaseDropdown";
import SimpleDropdown from "@/components/dropdowns/SimpleDropdown/SimpleDropdown";
import { cn } from "@/lib/utils";
import { MetaRatingComparisonsProp } from "@/pages/MetaRatingLandingPage/components/MetaRatingComparison";
import { contentLabelsSelector } from "@/redux/loadedContent/loadedContentSlice";
import { BREAKPOINTS } from "@/settings";
import { Trans, useTranslation } from "@repo/shared/i18n";
import React from "react";
import { useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

// Pitch order for sorting positions, goalkeeper to striker.
const uniquePositionScores: Record<string, number> = {
  GK: 0,
  RB: 1,
  LB: 5,
  CB: 6,
  CDM: 10,
  CM: 11,
  RM: 14,
  LM: 15,
  CAM: 16,
  RW: 17,
  LW: 20,
  ST: 21,
};

const MetaRatingFilterBar: React.FC<MetaRatingComparisonsProp> = ({
  rolesByPosition,
  selectedPosition,
  selectedRole,
  setSelectedPosition,
  setSelectedRole,
}) => {
  const { t } = useTranslation("metaRating");
  const isTouch = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const { positions: positionLabels } = useSelector(contentLabelsSelector);
  const positionDropdownOptions =
    Object.keys(rolesByPosition).length > 0
      ? Object.keys(rolesByPosition)
          ?.sort((a, b) => uniquePositionScores[b] - uniquePositionScores[a])
          ?.map((position) => ({
            label: positionLabels[position]?.name ?? position,
            value: position,
          }))
      : [];

  const roleDropdownOptions =
    Object.keys(rolesByPosition).length > 0 && selectedPosition?.value
      ? rolesByPosition[selectedPosition.value]?.map((role) => ({
          label: role.name,
          value: role.id.toString(),
        }))
      : [];

  return (
    <div className={cn("flex flex-col", isTouch ? "gap-6" : "bg-green-500")}>
      <div className={cn("flex flex-col", !isTouch && "mb-6")}>
        <label
          htmlFor="play-styles"
          className={cn(
            "text-dark-primary font-semibold",
            isTouch ? "text-xs" : "text-base font-bold",
          )}
        >
          {t("calc.title")}
        </label>

        <span
          id="play-styles"
          className={cn(
            "text-dark-tertiary",
            isTouch ? "w-full text-xs" : "text-sm",
          )}
        >
          {isTouch ? (
            <span>{t("calc.description")}</span>
          ) : (
            <Trans
              ns="metaRating"
              i18nKey="calc.descriptionDesktop"
              components={{ br: <br /> }}
            />
          )}
        </span>
      </div>

      <div className={cn("flex flex-row", !isTouch && "gap-6")}>
        <div className="flex gap-4">
          <SimpleDropdown
            variant="tertiary"
            options={positionDropdownOptions}
            selectedOption={
              positionDropdownOptions.find(
                (o) => o.value === selectedPosition?.value,
              ) ?? selectedPosition
            }
            onChange={setSelectedPosition}
            className="min-w-16"
            large
            align="right"
          />
          <SimpleDropdown
            variant="tertiary"
            options={roleDropdownOptions}
            selectedOption={
              roleDropdownOptions.find(
                (o) => o.value === selectedRole?.value,
              ) ?? selectedRole
            }
            onChange={setSelectedRole}
            className="min-w-32"
            large
            align="right"
          />
        </div>
      </div>
    </div>
  );
};

export default MetaRatingFilterBar;
